import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

const GalleryContainer = styled.main`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const GallerySection = styled.section`
  margin-bottom: 3rem;

  h2 {
    text-align: center;
    margin-bottom: 2rem;
    color: #8b4513;
  }
`;

const FilterButtons = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    gap: 0.5rem;
  }
`;

const FilterButton = styled.button`
  background-color: ${props => props.active ? '#d2691e' : '#f5f5dc'};
  border: 2px solid #d2691e;
  color: ${props => props.active ? 'white' : '#8b4513'};
  padding: 0.5rem 1rem;
  border-radius: 25px;
  cursor: pointer;
  transition: all 0.3s;
  font-weight: bold;

  @media (max-width: 768px) {
    padding: 0.4rem 0.8rem;
    font-size: 0.9rem;
  }

  &:hover {
    background-color: #d2691e;
    color: white;
  }
`;

const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
`;

const GalleryItem = styled(motion.div)`
  position: relative;
  overflow: hidden;
  border-radius: 10px;
  cursor: pointer;
  transition: transform 0.3s;

  &:hover {
    transform: scale(1.05);
  }

  img {
    width: 100%;
    height: 300px;
    object-fit: cover;
    transition: opacity 0.3s;

    &:hover {
      opacity: 0.8;
    }
  }
`;

const Overlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0,0,0,0.8));
  color: white;
  padding: 2rem 1rem 1rem;
  transform: translateY(100%);
  transition: transform 0.3s;

  ${GalleryItem}:hover & {
    transform: translateY(0);
  }

  h3 {
    color: white;
    margin-bottom: 0.5rem;
  }

  p {
    color: #ccc;
    font-size: 0.9rem;
  }
`;

const Lightbox = styled(motion.div)`
  position: fixed;
  z-index: 1000;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0,0,0,0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const LightboxContent = styled(motion.img)`
  max-width: 90%;
  max-height: 90%;
  object-fit: contain;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 35px;
  color: #f1f1f1;
  font-size: 40px;
  font-weight: bold;
  background: none;
  border: none;
  cursor: pointer;
  transition: color 0.3s;

  &:hover {
    color: #bbb;
  }
`;

const NavButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  color: #f1f1f1;
  font-size: 40px;
  font-weight: bold;
  background: none;
  border: none;
  cursor: pointer;
  transition: color 0.3s;

  &:hover {
    color: #bbb;
  }

  &.prev {
    left: 35px;

    @media (max-width: 768px) {
      left: 15px;
      font-size: 30px;
    }
  }

  &.next {
    right: 85px;

    @media (max-width: 768px) {
      right: 55px;
      font-size: 30px;
    }
  }
`;

const Caption = styled.div`
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  color: white;
  text-align: center;
  background: rgba(0,0,0,0.7);
  padding: 1rem;
  border-radius: 10px;
  max-width: 80%;

  h3 {
    margin: 0 0 0.5rem 0;
    color: white;
  }

  p {
    margin: 0;
    color: #ccc;
  }
`;

const Gallery = () => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [lightboxImage, setLightboxImage] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const galleryItems = [
    {
      id: 1,
      category: 'wedding',
      image: 'https://images.unsplash.com/photo-1578985545062-69928b1d9587?w=500',
      fullImage: 'https://images.unsplash.com/photo-1578985545062-69928b1d9587?w=1200',
      title: 'Elegant Wedding Cake',
      description: 'Three-tier vanilla cake with buttercream frosting and fresh flowers.'
    },
    {
      id: 2,
      category: 'birthday',
      image: 'https://images.unsplash.com/photo-1621303837174-89787a7d4729?w=500',
      fullImage: 'https://images.unsplash.com/photo-1621303837174-89787a7d4729?w=1200',
      title: 'Colorful Birthday Cake',
      description: 'Fun rainbow cake perfect for kids\' celebrations.'
    },
    {
      id: 3,
      category: 'specialty',
      image: 'https://images.unsplash.com/photo-1551024506-0bccd828d307?w=500',
      fullImage: 'https://images.unsplash.com/photo-1551024506-0bccd828d307?w=1200',
      title: 'Decadent Chocolate Cake',
      description: 'Rich chocolate layers with ganache and berries.'
    },
    {
      id: 4,
      category: 'corporate',
      image: 'https://images.unsplash.com/photo-1464349095431-e9a21285b5f3?w=500',
      fullImage: 'https://images.unsplash.com/photo-1464349095431-e9a21285b5f3?w=1200',
      title: 'Tiered Dessert Tower',
      description: 'Assortment of mini desserts for elegant events.'
    },
    {
      id: 5,
      category: 'birthday',
      image: 'https://images.unsplash.com/photo-1586985289688-ca3cf47d3e6e?w=500',
      fullImage: 'https://images.unsplash.com/photo-1586985289688-ca3cf47d3e6e?w=1200',
      title: 'Cupcake Display',
      description: 'Variety of flavors for any occasion.'
    },
    {
      id: 6,
      category: 'specialty',
      image: 'https://images.unsplash.com/photo-1606312619076-2246d3594039?w=500',
      fullImage: 'https://images.unsplash.com/photo-1606312619076-2246d3594039?w=1200',
      title: 'French Macarons',
      description: 'Delicate almond cookies in various flavors.'
    },
    {
      id: 7,
      category: 'wedding',
      image: 'https://images.unsplash.com/photo-1535254973040-607b474cb50d?w=500',
      fullImage: 'https://images.unsplash.com/photo-1535254973040-607b474cb50d?w=1200',
      title: 'Rustic Wedding Cake',
      description: 'Naked cake with fresh greenery and wildflowers.'
    },
    {
      id: 8,
      category: 'birthday',
      image: 'https://images.unsplash.com/photo-1542826438-bd32f43d686f?w=500',
      fullImage: 'https://images.unsplash.com/photo-1542826438-bd32f43d686f?w=1200',
      title: 'Princess Birthday Cake',
      description: 'Pink castle cake for little princesses.'
    },
    {
      id: 9,
      category: 'corporate',
      image: 'https://images.unsplash.com/photo-1488477304112-4944851de03d?w=500',
      fullImage: 'https://images.unsplash.com/photo-1488477304112-4944851de03d?w=1200',
      title: 'Corporate Dessert Platter',
      description: 'Professional presentation for business events.'
    },
    {
      id: 10,
      category: 'specialty',
      image: 'https://images.unsplash.com/photo-1481391032119-d89fee407e44?w=500',
      fullImage: 'https://images.unsplash.com/photo-1481391032119-d89fee407e44?w=1200',
      title: 'Seasonal Fruit Tart',
      description: 'Fresh berries and cream on buttery pastry.'
    },
    {
      id: 11,
      category: 'wedding',
      image: 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=500',
      fullImage: 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=1200',
      title: 'Modern Geometric Cake',
      description: 'Clean lines and contemporary design.'
    },
    {
      id: 12,
      category: 'birthday',
      image: 'https://images.unsplash.com/photo-1535141192574-5d4897c12636?w=500',
      fullImage: 'https://images.unsplash.com/photo-1535141192574-5d4897c12636?w=1200',
      title: 'Graduation Cake',
      description: 'Congratulations with edible diploma toppers.'
    }
  ];

  const filteredItems = activeFilter === 'all'
    ? galleryItems
    : galleryItems.filter(item => item.category === activeFilter);

  const openLightbox = (index) => {
    setCurrentImageIndex(index);
    setLightboxImage(filteredItems[index]);
  };

  const closeLightbox = () => {
    setLightboxImage(null);
  };

  const nextImage = () => {
    const nextIndex = (currentImageIndex + 1) % filteredItems.length;
    setCurrentImageIndex(nextIndex);
    setLightboxImage(filteredItems[nextIndex]);
  };

  const prevImage = () => {
    const prevIndex = (currentImageIndex - 1 + filteredItems.length) % filteredItems.length;
    setCurrentImageIndex(prevIndex);
    setLightboxImage(filteredItems[prevIndex]);
  };

  const handleKeyPress = (e) => {
    if (!lightboxImage) return;

    if (e.key === 'ArrowRight') {
      nextImage();
    } else if (e.key === 'ArrowLeft') {
      prevImage();
    } else if (e.key === 'Escape') {
      closeLightbox();
    }
  };

  React.useEffect(() => {
    if (lightboxImage) {
      document.addEventListener('keydown', handleKeyPress);
      return () => document.removeEventListener('keydown', handleKeyPress);
    }
  }, [lightboxImage, currentImageIndex]);

  return (
    <GalleryContainer>
      <GallerySection>
        <h2>Our Custom Creations</h2>
        <FilterButtons>
          {['all', 'wedding', 'birthday', 'corporate', 'specialty'].map(filter => (
            <FilterButton
              key={filter}
              active={activeFilter === filter}
              onClick={() => setActiveFilter(filter)}
            >
              {filter.charAt(0).toUpperCase() + filter.slice(1)}
            </FilterButton>
          ))}
        </FilterButtons>
        <GalleryGrid>
          <AnimatePresence>
            {filteredItems.map((item, index) => (
              <GalleryItem
                key={item.id}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.3 }}
                onClick={() => openLightbox(index)}
                whileHover={{ scale: 1.05 }}
              >
                <img src={item.image} alt={item.title} />
                <Overlay>
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </Overlay>
              </GalleryItem>
            ))}
          </AnimatePresence>
        </GalleryGrid>
      </GallerySection>

      <AnimatePresence>
        {lightboxImage && (
          <Lightbox
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeLightbox}
          >
            <CloseButton onClick={closeLightbox}>&times;</CloseButton>
            <NavButton className="prev" onClick={(e) => { e.stopPropagation(); prevImage(); }}>&#10094;</NavButton>
            <NavButton className="next" onClick={(e) => { e.stopPropagation(); nextImage(); }}>&#10095;</NavButton>
            <LightboxContent
              src={lightboxImage.fullImage}
              alt={lightboxImage.title}
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.3 }}
            />
            <Caption>
              <h3>{lightboxImage.title}</h3>
              <p>{lightboxImage.description}</p>
            </Caption>
          </Lightbox>
        )}
      </AnimatePresence>
    </GalleryContainer>
  );
};

export default Gallery;